import React from 'react';
import { Calendar, Percent, DollarSign, CreditCard, Landmark } from 'lucide-react';

/**
 * Компонент формы для ввода данных о новом кредите (рефинансировании)
 */
const NewLoanSection = ({ 
  formData, 
  handleInputChange, 
  errors,
  selectedBank
}) => {
  return (
    <div className="mb-6 bg-gradient-to-r from-green-50 to-teal-50 dark:from-green-900/10 dark:to-teal-900/10 p-5 rounded-lg border border-green-200 dark:border-green-800/50 shadow-sm">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
        <Landmark className="w-5 h-5 text-green-500" />
        Новый кредит (рефинансирование)
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Новая процентная ставка */}
        <div>
          <label htmlFor="newInterestRate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Percent className="w-4 h-4 mr-1 text-green-500" />
            Новая ставка (%)
            {selectedBank && (
              <span className="ml-2 text-xs font-normal text-green-600 dark:text-green-400">
                от {selectedBank.minRate}% в {selectedBank.name}
              </span>
            )}
          </label>
          <input
            type="text"
            id="newInterestRate"
            name="newInterestRate"
            value={formData.newInterestRate}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.newInterestRate ? 'input-error' : ''}`}
            placeholder="Введите ставку"
          />
          {errors.newInterestRate && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.newInterestRate}
            </p>
          )}
        </div>

        {/* Новый срок кредита */}
        <div>
          <label htmlFor="newLoanTerm" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Calendar className="w-4 h-4 mr-1 text-green-500" />
            Новый срок (лет)
          </label>
          <input
            type="text"
            id="newLoanTerm"
            name="newLoanTerm"
            value={formData.newLoanTerm}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.newLoanTerm ? 'input-error' : ''}`}
            placeholder="Введите срок"
          />
          {errors.newLoanTerm && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.newLoanTerm}
            </p>
          )}
        </div>

        {/* Дополнительная сумма */}
        <div>
          <label htmlFor="additionalAmount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <DollarSign className="w-4 h-4 mr-1 text-green-500" />
            Дополнительная сумма
          </label>
          <input
            type="text"
            id="additionalAmount"
            name="additionalAmount"
            value={formData.additionalAmount}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.additionalAmount ? 'input-error' : ''}`}
            placeholder="Введите сумму"
          />
          {errors.additionalAmount && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.additionalAmount}
            </p>
          )}
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Дополнительные средства, которые вы хотите получить вместе с рефинансированием
          </p>
        </div>

        {/* Дата начала нового кредита */}
        <div>
          <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
            <Calendar className="w-4 h-4 mr-1 text-green-500" />
            Дата начала кредита
          </label>
          <input
            type="date"
            id="startDate"
            name="startDate"
            value={formData.startDate}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.startDate ? 'input-error' : ''}`}
          />
          {errors.startDate && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.startDate}
            </p>
          )}
        </div>

        {/* Тип платежа для нового кредита */}
        <div className="col-span-full">
          <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Тип платежа для нового кредита
          </span>
          <div className="grid grid-cols-2 gap-2">
            <label className="inline-flex items-center">
              <input
                type="radio"
                name="newPaymentType"
                value="annuity"
                checked={formData.newPaymentType === 'annuity'}
                onChange={handleInputChange}
                className="form-radio"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Аннуитетный (равные платежи)
              </span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                name="newPaymentType"
                value="differentiated"
                checked={formData.newPaymentType === 'differentiated'}
                onChange={handleInputChange}
                className="form-radio"
              />
              <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Дифференцированный (уменьшающийся)
              </span>
            </label>
          </div>
          {errors.newPaymentType && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.newPaymentType}
            </p>
          )}
        </div>
      </div>

      <div className="mt-4 text-sm text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-900/30 p-3 rounded-lg border border-green-200 dark:border-green-700">
        <div className="flex items-start">
          <CreditCard className="w-5 h-5 text-green-600 dark:text-green-400 mr-2 mt-0.5" />
          <div>
            <p className="font-medium">Советы по рефинансированию</p>
            <p className="text-xs mt-1">
              {selectedBank 
                ? `В ${selectedBank.name} вы можете рефинансировать кредит на срок до ${selectedBank.maxTerm} лет с минимальной ставкой от ${selectedBank.minRate}%.` 
                : 'Выберите банк в разделе "Предложения банков", чтобы узнать актуальные условия рефинансирования.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewLoanSection;